/**
 * What the photo shrink did to one blob, for the upload record (speed plan
 * 2026-10-19, packet P1; netlify/functions/lib/uploadRecord.mjs).
 *
 * downscaleImage (lib/offerings.ts) notes each blob it hands back here, and
 * the upload gateway looks the blob up when it finalizes. No caller has to
 * pass anything along, so every upload door gets the answer for free:
 *   - shrunk true: the photo was re-encoded to the smaller JPEG.
 *   - shrunk false: the shrink ran and kept the original (already small and
 *     a compact type, or this browser could not decode it).
 *   - 'timeout': the shrink ran out of time (lib/photoShrink.ts deadline) and
 *     the original was sent as picked. Not the same as false: nothing chose
 *     to keep it, the phone could not get it ready in time.
 *   - 'skipped': no shrink ran at all (a video, a file, a GIF, or a door
 *     that does not shrink).
 * shrinkMs is the phone's own clock from asking for the shrink to getting
 * the blob back, including any wait for the one-at-a-time shrink turn.
 *
 * A WeakMap, so a blob that is dropped takes its note with it.
 */

export type PhotoShrinkOutcome = boolean | 'skipped' | 'timeout';

interface ShrinkNote {
  shrunk: boolean | 'timeout';
  shrinkMs: number;
}

const notes = new WeakMap<Blob, ShrinkNote>();

export function notePhotoShrink(blob: Blob, shrunk: boolean | 'timeout', shrinkMs: number): void {
  try {
    notes.set(blob, { shrunk, shrinkMs: Math.max(0, Math.round(shrinkMs)) });
  } catch {
    // A note is diagnosis only; it must never fail a photo.
  }
}

export function photoShrinkOutcome(blob: Blob): { shrunk: PhotoShrinkOutcome; shrinkMs: number | null } {
  const note = notes.get(blob);
  return note ? { shrunk: note.shrunk, shrinkMs: note.shrinkMs } : { shrunk: 'skipped', shrinkMs: null };
}
